import { useMemo, useRef, useEffect, useCallback } from 'react';

import { useDroppable } from '@dnd-kit/core';
import PropTypes from 'prop-types';
import { BsPlus } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';

import { useStageDealsInfinite } from '../../../models/deals/queries';
import { formatEuro } from '../../../utils/formatters';

import SortableDealCard from './SortableDealCard';

const StageColumn = ({
	stage,
	pipelineId,
	filters,
	stageSummary,
	onDealsLoaded,
	onOpenDeal,
	onNewDeal,
	initialScrollTop,
	onScrollChange,
}) => {
	const { setNodeRef, isOver } = useDroppable({
		id: `stage-${stage.id}`,
		data: { type: 'stage', stageId: stage.id },
	});

	const cardsRef = useRef(null);
	const rafRef = useRef(null);
	const sentinelRef = useRef(null);

	// Own data fetch
	const {
		data: dealsData,
		isLoading,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
	} = useStageDealsInfinite(pipelineId, stage.id, filters);

	// The backend returns deals already ordered by next-activity due date and
	// paginated, so the merged pages are display-ready — no client-side sort.
	const displayDeals = useMemo(() => dealsData?.pages?.flatMap((p) => p.deals) ?? [], [dealsData]);

	// Report loaded deals to KanbanBoard for drag coordination
	useEffect(() => {
		onDealsLoaded(stage.id, displayDeals);
	}, [stage.id, displayDeals, onDealsLoaded]);

	// IntersectionObserver for infinite scroll
	useEffect(() => {
		if (!hasNextPage || isFetchingNextPage) return;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) fetchNextPage();
			},
			{ root: cardsRef.current, rootMargin: '200px' },
		);
		if (sentinelRef.current) observer.observe(sentinelRef.current);
		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	// Restore scroll position on mount
	const initialScrollRef = useRef(initialScrollTop);
	useEffect(() => {
		if (cardsRef.current && initialScrollRef.current) {
			cardsRef.current.scrollTop = initialScrollRef.current;
		}
	}, []);

	const handleScroll = useCallback(() => {
		if (!onScrollChange || rafRef.current) return;
		rafRef.current = requestAnimationFrame(() => {
			if (cardsRef.current) {
				onScrollChange(stage.id, cardsRef.current.scrollTop);
			}
			rafRef.current = null;
		});
	}, [onScrollChange, stage.id]);

	useEffect(
		() => () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
		},
		[],
	);

	// Header stats from summary (true totals) or fallback to loaded data
	const totalCount = stageSummary?.totalCount ?? displayDeals.length;
	const totalValue = stageSummary?.totalValue ?? displayDeals.reduce((sum, d) => sum + Number(d.dealValue ?? 0), 0);

	return (
		<div
			ref={setNodeRef}
			className={`flex flex-col w-[280px] min-w-[280px] max-h-full flex-shrink-0 overflow-hidden rounded-card border bg-white transition-colors ${
				isOver ? 'border-primary-300 ring-2 ring-primary-300' : 'border-bgBase-500'
			}`}
		>
			<div className="px-4 py-3 border-b border-bgBase-500 bg-bgBase-300">
				<div className="flex items-center justify-between gap-2">
					<h3 className="text-xs-head text-text truncate">{stage.name}</h3>
					<span className="flex-shrink-0 text-sm text-primary-400">{totalCount} Deals</span>
				</div>
				<span className="text-sm-bold text-bgBase-800 tabular-nums">{formatEuro(totalValue)}</span>
			</div>

			<div
				ref={cardsRef}
				onScroll={handleScroll}
				className={`flex-1 p-3 space-y-3 overflow-y-auto ${isOver ? 'bg-primary-100' : ''}`}
			>
				{isLoading ? (
					<div className="flex items-center justify-center py-8">
						<div className="w-5 h-5 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
					</div>
				) : displayDeals.length === 0 ? (
					<div className="border-2 border-dashed border-bgBase-500 rounded-card p-6 text-center text-xs text-bgBase-800">
						Ziehe einen Deal hierher
					</div>
				) : (
					displayDeals.map((deal) => <SortableDealCard key={deal.id} deal={deal} onOpen={onOpenDeal} />)
				)}
				{hasNextPage && <div ref={sentinelRef} className="h-1" />}
				{isFetchingNextPage && (
					<div className="flex items-center justify-center py-3">
						<div className="w-4 h-4 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
					</div>
				)}
				{onNewDeal && (
					<Button variant="outline" fullWidth onClick={onNewDeal}>
						<BsPlus className="w-4 h-4" /> Deal hinzufügen
					</Button>
				)}
			</div>
		</div>
	);
};

StageColumn.propTypes = {
	stage: PropTypes.object.isRequired,
	stageIndex: PropTypes.number,
	pipelineId: PropTypes.string.isRequired,
	filters: PropTypes.object,
	stageSummary: PropTypes.object,
	onDealsLoaded: PropTypes.func.isRequired,
	onOpenDeal: PropTypes.func,
	onNewDeal: PropTypes.func,
	initialScrollTop: PropTypes.number,
	onScrollChange: PropTypes.func,
};

export default StageColumn;
